import { z } from 'zod'

/**
 * Runtime configuration.
 *
 * The bundle is environment-free: CDK writes /config.json into the site bucket
 * at deploy time, so the same artifact can be promoted between stages. Nothing
 * here may move into import.meta.env.
 */
export const RuntimeConfigSchema = z.object({
  /**
   * Origin prefix for the API, NOT including /api. Every path the client builds
   * already starts with /api, so in production this is the empty string: the SPA
   * and the API are served from the same CloudFront distribution. Setting it to
   * "/api" produces /api/api/jobs and a 404 on every call, which is exactly what
   * happened once, so the empty string has to be a legal value rather than a
   * validation failure.
   */
  apiUrl: z.string(),
  userPoolId: z.string().min(1),
  userPoolClientId: z.string().min(1),
  cognitoDomain: z.string().min(1),
  region: z.string().min(1),
  entraEnabled: z.boolean(),
})
export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>

export class ConfigError extends Error {}

/** `https://d.auth.ap-south-1.amazoncognito.com/` and `d.auth...` both arrive in practice. */
function normaliseDomain(raw: string): string {
  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
  return withScheme.replace(/\/+$/, '')
}

/**
 * Trailing slash here doubles up against every path we build. A value of "/api"
 * is always a mistake for the same reason, so it is corrected rather than
 * silently producing /api/api/... on every request.
 */
function normaliseApiUrl(raw: string): string {
  const trimmed = raw.replace(/\/+$/, '')
  return trimmed === '/api' ? '' : trimmed
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  // no-store because CloudFront caching this file across a redeploy would point
  // a fresh bundle at a retired API or an old user pool client.
  const res = await fetch('/config.json', { cache: 'no-store' })
  if (!res.ok) {
    throw new ConfigError(`/config.json returned ${res.status}. It is written at deploy time.`)
  }

  // The SPA routing function rewrites unknown paths to index.html, so a missing
  // config.json arrives as HTML rather than as a 404. Say that plainly instead
  // of letting an "Unexpected token <" reach the user.
  const body = await res.text()
  let parsed: unknown
  try {
    parsed = JSON.parse(body)
  } catch {
    throw new ConfigError('/config.json is not JSON. The deploy step that writes it has not run.')
  }

  const result = RuntimeConfigSchema.safeParse(parsed)
  if (!result.success) {
    const missing = result.error.issues.map((i) => i.path.join('.')).join(', ')
    throw new ConfigError(`/config.json is incomplete: ${missing}`)
  }

  return {
    ...result.data,
    apiUrl: normaliseApiUrl(result.data.apiUrl),
    cognitoDomain: normaliseDomain(result.data.cognitoDomain),
  }
}

/** Cognito's OIDC issuer. The hosted-UI domain is a separate thing entirely. */
export function oidcAuthority(config: RuntimeConfig): string {
  return `https://cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`
}
